import { settingsSections } from "../routing/settingsSections";

export const defaultAuthDestination = "/";
export type AuthRoute = "login" | "signup";

export function authRoute(pathname: string): AuthRoute | null {
  if (pathname === "/login" || pathname.startsWith("/login/")) return "login";
  if (pathname === "/signup" || pathname.startsWith("/signup/")) return "signup";
  return null;
}

const appOrigin = "https://app.signalbill.com";
const productPath = /^\/(?:invoices|estimates|clients|payments|reports)(?:\/|$)|^\/release-notes$|^\/settings\/billing$/;
const settingsPaths = new Set<string>(["/settings", ...settingsSections.map(section => section.path)]);

/** The query is navigation state only; account identity always comes from the server. */
export function safeAuthDestination(value: string | null | undefined): string {
  if (!value || /[\u0000-\u001f\u007f]/.test(value)) return defaultAuthDestination;
  let url: URL;
  try { url = new URL(value, appOrigin); } catch { return defaultAuthDestination; }
  if (!value.startsWith("/") || value.startsWith("//") || value.includes("\\") || url.origin !== appOrigin) {
    return defaultAuthDestination;
  }

  // Inspect every encoded pathname layer, including encoded slashes, dot segments,
  // and auth paths. Keep the original query/hash rather than decoding user filters.
  let pathname = url.pathname;
  for (let depth = 0; depth < 8; depth += 1) {
    if (/\s|\\|[\u0000-\u001f\u007f?#]/.test(pathname) || pathname.startsWith("//")) return defaultAuthDestination;
    const normalized = new URL(pathname, appOrigin);
    if (normalized.origin !== appOrigin || authRoute(normalized.pathname)) return defaultAuthDestination;
    if (normalized.pathname !== "/" && !productPath.test(normalized.pathname) && !settingsPaths.has(normalized.pathname)) return defaultAuthDestination;
    let decoded: string;
    try { decoded = decodeURIComponent(pathname); } catch { return defaultAuthDestination; }
    if (decoded === pathname) {
      return url.pathname === "/" ? defaultAuthDestination : `${url.pathname}${url.search}${url.hash}`;
    }
    pathname = decoded;
  }
  return defaultAuthDestination;
}

export function destinationFromSearch(search: string): string {
  return safeAuthDestination(new URLSearchParams(search).get("redirect"));
}

export function authUrl(route: AuthRoute, destination?: string): string {
  const safe = safeAuthDestination(destination);
  return safe === defaultAuthDestination ? `/${route}` : `/${route}?${new URLSearchParams({ redirect: safe })}`;
}
